import { useEffect, useState } from "react";
import { saveFiche } from "./api";
import { createClient, loadCompanies, lookupSiret, readClient, searchClients, type FxClient, type FxCompany } from "./facturx";
import type { CrmFiche } from "./types";

type Kind = "particulier" | "micro" | "societe";

type Draft = {
  kind: Kind;
  name: string;
  siret: string;
  siren: string;
  vat_number: string;
  email: string;
  phone: string;
  address_line: string;
  postal_code: string;
  city: string;
  sirene_status: string;
  sirene_closed_on: string;
  sirene_created_on: string;
};

const EMPTY: Draft = {
  kind: "societe",
  name: "",
  siret: "",
  siren: "",
  vat_number: "",
  email: "",
  phone: "",
  address_line: "",
  postal_code: "",
  city: "",
  sirene_status: "",
  sirene_closed_on: "",
  sirene_created_on: "",
};

function gaps(d: Draft): string[] {
  const missing: string[] = [];
  if (!d.name.trim()) missing.push("nom du client");
  if (!d.address_line.trim() || !d.postal_code.trim() || !d.city.trim()) {
    missing.push("adresse complète du client (rue, code postal, ville)");
  }
  const siret = d.siret.replace(/\D/g, "");
  const siren = d.siren.replace(/\D/g, "");
  if (d.kind === "micro") {
    if (siret.length !== 14 && siren.length !== 9) missing.push("SIRET (14) ou SIREN (9) du client micro");
  } else if (d.kind === "societe") {
    if (siret.length !== 14) missing.push("SIRET (14 chiffres) du client");
    if (siren.length !== 9) missing.push("SIREN (9 chiffres) du client");
    if (!d.vat_number.trim()) missing.push("n° de TVA du client (ou type Micro)");
  }
  return missing;
}

export function FactureTab({
  fiche,
  onChange,
  onLinked,
}: {
  fiche: CrmFiche;
  onChange: (fiche: CrmFiche) => void;
  onLinked?: () => void;
}) {
  const [companies, setCompanies] = useState<FxCompany[]>([]);
  const [companyId, setCompanyId] = useState(0);
  const [mode, setMode] = useState<"search" | "create">("search");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<{ id: number; name: string; siret?: string | null }[]>([]);
  const [picked, setPicked] = useState(0);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [missing, setMissing] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadCompanies()
      .then((data) => {
        const rows = data.companies || [];
        setCompanies(rows);
        setCompanyId(fiche.facturx_company_id || Number(rows[0]?.id || 0));
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Factures ne répond pas."));
  }, [fiche.facturx_company_id]);

  async function adopt(client: FxClient) {
    onChange(
      await saveFiche({
        ...fiche,
        name: client.name || fiche.name,
        email: client.email || "",
        phone: client.phone || "",
        address: client.address_line || "",
        postal_code: client.postal_code || "",
        city: client.city || "",
        facturx_company_id: client.company_id,
        facturx_client_id: client.id,
      }),
    );
    onLinked?.();
  }

  async function onSearch() {
    setBusy(true);
    setError("");
    try {
      const data = await searchClients(companyId, query.trim());
      setHits(data.clients || []);
      setPicked(0);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Recherche impossible");
    } finally {
      setBusy(false);
    }
  }

  async function onImport(id: number) {
    setBusy(true);
    setError("");
    try {
      const data = await readClient(id);
      await adopt(data.client);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Import impossible");
    } finally {
      setBusy(false);
    }
  }

  async function onLookup() {
    const q = draft.siret.trim() || draft.name.trim();
    if (!q) {
      setError("Indique un SIRET ou un nom.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await lookupSiret(q);
      const match = data.match;
      setDraft({
        ...draft,
        name: String(match.name || draft.name),
        siret: String(match.siret || draft.siret),
        siren: String(match.siren || draft.siren),
        vat_number: draft.kind === "micro" ? "" : String(match.vat_number || draft.vat_number),
        address_line: String(match.address_line || draft.address_line),
        postal_code: String(match.postal_code || draft.postal_code),
        city: String(match.city || draft.city),
        sirene_status: String(match.sirene_status || ""),
        sirene_closed_on: String(match.sirene_closed_on || ""),
        sirene_created_on: String(match.sirene_created_on || ""),
      });
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Aucun établissement trouvé.");
    } finally {
      setBusy(false);
    }
  }

  async function onCreate() {
    const holes = gaps(draft);
    setMissing(holes);
    if (holes.length || !companyId) return;
    setBusy(true);
    setError("");
    try {
      const data = await createClient({
        company_id: companyId,
        kind: draft.kind,
        name: draft.name.trim(),
        siret: draft.kind === "particulier" ? "" : draft.siret,
        siren: draft.kind === "particulier" ? "" : draft.siren,
        vat_number: draft.kind === "societe" ? draft.vat_number : "",
        tax_exempt: draft.kind === "micro",
        email: draft.email,
        phone: draft.phone,
        address_line: draft.address_line,
        postal_code: draft.postal_code,
        city: draft.city,
        country_code: "FR",
        sirene_status: draft.sirene_status || undefined,
        sirene_closed_on: draft.sirene_closed_on,
        sirene_created_on: draft.sirene_created_on,
      });
      await adopt(data.client);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Création impossible");
    } finally {
      setBusy(false);
    }
  }

  const companyName = (row: FxCompany) => row.legal_name || row.name || `Société ${row.id}`;

  return (
    <div className="text-sm">
        <p className="text-slate-500">
          {fiche.facturx_client_id ? `Déjà le client FacturX n° ${fiche.facturx_client_id}.` : "Le client naît dans Factures. La fiche CRM se remplit ensuite."}
        </p>
        {error ? <p className="mt-4 text-center text-sm text-rose-700">{error}</p> : null}
        {companies.length === 0 ? <p className="mt-6 text-center text-sm text-slate-500">Aucune société Factures pour ce compte.</p> : null}
        {companies.length > 1 ? (
          <label className="mt-5 block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-500">Société Factures</span>
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2" value={companyId} onChange={(event) => setCompanyId(Number(event.target.value))}>
              {companies.map((row) => (
                <option key={row.id} value={row.id}>{companyName(row)}</option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="mt-5 flex justify-center gap-2">
          <button type="button" className={chip(mode === "search")} onClick={() => setMode("search")}>Rechercher</button>
          <button type="button" className={chip(mode === "create")} onClick={() => setMode("create")}>Créer</button>
        </div>
        {mode === "search" ? (
          <div className="mt-5">
            <div className="flex gap-2">
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nom ou SIRET" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <button type="button" disabled={busy || !companyId} className="rounded-full crm-fill px-4 text-sm font-semibold text-white" onClick={() => void onSearch()}>
                Chercher
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-slate-400">Clic pour choisir, double-clic pour lier.</p>
            <ul className="mt-2 space-y-1">
              {hits.map((hit, index) => (
                <li key={hit.id} className="crm-rise" style={{ animationDelay: `${index * 0.05}s` }}>
                  <button type="button" className={picked === hit.id ? "crm-soft w-full rounded-xl px-3 py-2 text-left text-sm font-semibold" : "w-full rounded-xl px-3 py-2 text-left text-sm hover:bg-black/5"} onClick={() => setPicked(hit.id)} onDoubleClick={() => void onImport(hit.id)}>
                    <span className="font-medium">{hit.name}</span>
                    {hit.siret ? <span className="ml-2 text-slate-400">{hit.siret}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-5 grid gap-3">
            <div className="flex justify-center gap-2">
              {(["particulier", "micro", "societe"] as Kind[]).map((kind) => (
                <button key={kind} type="button" className={chip(draft.kind === kind)} onClick={() => setDraft({ ...draft, kind, vat_number: kind === "societe" ? draft.vat_number : "" })}>
                  {kind === "particulier" ? "Particulier" : kind === "micro" ? "Micro" : "Société"}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={draft.siret} onChange={(event) => setDraft({ ...draft, siret: event.target.value })} placeholder="SIRET ou raison sociale" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <button type="button" disabled={busy} className="rounded-full bg-slate-800 px-4 text-sm font-semibold text-white" onClick={() => void onLookup()}>
                INSEE
              </button>
            </div>
            <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="Nom" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            {draft.kind !== "particulier" ? <input value={draft.siren} onChange={(event) => setDraft({ ...draft, siren: event.target.value })} placeholder="SIREN" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" /> : null}
            {draft.kind === "societe" ? <input value={draft.vat_number} onChange={(event) => setDraft({ ...draft, vat_number: event.target.value })} placeholder="N° de TVA" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" /> : null}
            <input value={draft.address_line} onChange={(event) => setDraft({ ...draft, address_line: event.target.value })} placeholder="Adresse" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <input value={draft.postal_code} onChange={(event) => setDraft({ ...draft, postal_code: event.target.value })} placeholder="Code postal" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <input value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} placeholder="Ville" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <input value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} placeholder="E-mail" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            <input value={draft.phone} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} placeholder="Téléphone" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            {missing.length ? <p className="text-sm text-rose-700">Enregistrement impossible : {missing.join(" · ")}.</p> : null}
            <button type="button" disabled={busy || !companyId} className="rounded-full crm-fill px-5 py-2.5 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-slate-500" onClick={() => void onCreate()}>
              Créer dans Factures
            </button>
          </div>
        )}
    </div>
  );
}

function chip(active: boolean): string {
  return active
    ? "rounded-full crm-soft px-3 py-1 text-sm font-semibold crm-ink"
    : "rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600";
}
